import React, { useState } from 'react';
import Modal from 'react-modal';
import { ClipLoader } from 'react-spinners';
import axios from 'axios';
import { waitForTransaction, writeContract } from '@wagmi/core'
import { config } from '../config.jsx'
import { getUSDTAddress } from '../utils/addressHelpers.ts';
import { useAccount } from 'wagmi';
import TokenAbi from '../config/TokenAbi.json'
import { adminAddress, apiUrl } from '../utils/constants.ts'

Modal.setAppElement('#root');

interface KYCFormData {
  // Step 1
  projectName: string;
  tokenName: string;
  tokenAddress: string;
  projectDescription: string;

  // Step 2
  fullName: string;
  email: string;
  contactHandle: string;
  country: string;
  idFile: File | null;
  selfieFile: File | null;

  // Step 3
  website: string;
  twitter: string;
  github: string;
  previousProjects: string;
  justification: string;

  // Step 4
  consent1: boolean;
  consent2: boolean;
  consent3: boolean;
  signature: string;
  date: string;
}

interface KYCModalProps {
  isOpen: boolean;
  onClose: () => void;
  tokenName?: string;
  tokenAddress?: string;
}

const KYCModal: React.FC<KYCModalProps> = ({ isOpen, onClose, tokenName, tokenAddress }) => {
  const { chainId } = useAccount();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<KYCFormData>({
    projectName: tokenName || '',
    tokenName: '',
    tokenAddress: tokenAddress || '',
    projectDescription: '',
    fullName: '',
    email: '',
    contactHandle: '',
    country: '',
    idFile: null,
    selfieFile: null,
    website: '',
    twitter: '',
    github: '',
    previousProjects: '',
    justification: '',
    consent1: false,
    consent2: false,
    consent3: false,
    signature: '',
    date: new Date().toISOString().split('T')[0]
  });

  const [filePreviews, setFilePreviews] = useState({
    idFile: null as string | null,
    selfieFile: null as string | null
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, field: keyof KYCFormData) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFormData(prev => ({
        ...prev,
        [field]: file
      }));

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setFilePreviews(prev => ({
        ...prev,
        [field]: previewUrl
      }));
    }
  };

  const validateStep = (currentStep: number): boolean => {
    switch (currentStep) {
      case 1:
        return (
          formData.projectName.trim() !== '' &&
          formData.tokenName.trim() !== '' &&
          formData.projectDescription.trim() !== '' &&
          formData.projectDescription.split(' ').length >= 5
        );
      case 2:
        return (
          formData.fullName.trim() !== '' &&
          formData.email.trim() !== '' &&
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) &&
          formData.contactHandle.trim() !== '' &&
          formData.country.trim() !== '' &&
          formData.idFile !== null &&
          formData.selfieFile !== null
        );
      case 3:
        return (
          formData.website.trim() !== '' &&
          formData.twitter.trim() !== '' &&
          formData.justification.trim() !== '' &&
          formData.justification.length <= 200
        );
      case 4:
        return (
          formData.consent1 &&
          formData.consent2 &&
          formData.consent3 &&
          formData.signature.trim() !== ''
        );
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    setStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (validateStep(4)) {
      setIsSubmitting(true);
      try {
        let transferring = await writeContract(config, {
          address: getUSDTAddress(chainId) as `0x${string}`,
          abi: TokenAbi,
          functionName: 'transfer',
          args: [adminAddress, (299 * (10 ** 18)).toFixed(0)],
        })
        await waitForTransaction(config, {
          hash: transferring,
        })
        // Create FormData for file upload
        const uploadFormData = new FormData();
        if (formData.idFile) uploadFormData.append('idFile', formData.idFile);
        if (formData.selfieFile) uploadFormData.append('selfieFile', formData.selfieFile);
        uploadFormData.append('tokenAddress', formData.tokenAddress);

        // Upload files first
        const uploadResponse = await axios.post(`${apiUrl}/api/uploadKycFiles`, uploadFormData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        // Prepare data for submission (excluding files)
        const { idFile, selfieFile, ...submissionData } = formData;

        // Submit all data with file URLs
        await axios.post(`${apiUrl}/api/submitKYC`, {
          ...submissionData,
          idFileUrl: uploadResponse.data.idFileUrl,
          selfieFileUrl: uploadResponse.data.selfieFileUrl
        });

        alert('KYC submitted successfully!');
        onClose();
        window.location.reload();
      } catch (error) {
        console.error('Submission error:', error);
        alert('Failed to submit KYC. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const customStyles = {
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      zIndex: 1000,
    },
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      backgroundColor: '#1a1a2e',
      border: '1px solid #2d3748',
      borderRadius: '0.5rem',
      padding: '2rem',
      maxWidth: '600px',
      width: '90%',
      color: 'white',
      height: '90%',
    },
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={customStyles}
      contentLabel="KYC Verification Form"
    >
      <div className="flex flex-col space-y-6">
        <h2 className="text-2xl font-bold text-center text-blue-300">KYC Verification Form – GoldCheck Mark</h2>

        {/* Progress Steps */}
        <div className="flex justify-between items-center mb-6">
          {[1, 2, 3, 4].map((stepNumber) => (
            <div key={stepNumber} className="flex flex-col items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center
                  ${step === stepNumber ? 'bg-[#45c0be] text-white' :
                    step > stepNumber ? 'bg-green-500 text-white' : 'bg-gray-700 text-gray-300'}`}
              >
                {stepNumber}
              </div>
              <div className="text-xs mt-1 text-gray-400">
                {stepNumber === 1 && 'Project'}
                {stepNumber === 2 && 'Identity'}
                {stepNumber === 3 && 'Links'}
                {stepNumber === 4 && 'Consent'}
              </div>
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="space-y-4">
          {step === 1 && (
            <>
              <h3 className="text-xl font-semibold text-blue-200">Project Information</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Project Name</label>
                  <input
                    type="text"
                    name="projectName"
                    value={formData.projectName}
                    onChange={handleChange}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Token Name / Ticker</label>
                  <input
                    type="text"
                    name="tokenName"
                    value={formData.tokenName}
                    onChange={handleChange}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Token Contract Address</label>
                  <input
                    type="text"
                    name="tokenAddress"
                    value={formData.tokenAddress}
                    onChange={handleChange}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Brief Project Description (2–3 sentences)</label>
                  <textarea
                    name="projectDescription"
                    value={formData.projectDescription}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h3 className="text-xl font-semibold text-blue-200">Developer / Founder Information</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Full Legal Name</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Telegram or Discord Handle</label>
                  <input
                    type="text"
                    name="contactHandle"
                    value={formData.contactHandle}
                    onChange={handleChange}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Country of Residence</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Government-Issued ID Upload</label>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => handleFileChange(e, 'idFile')}
                    className="w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-500 file:text-white hover:file:bg-[#45c0be]"
                    required
                  />
                  {filePreviews.idFile && (
                    <div className="mt-2">
                      <img src={filePreviews.idFile} alt="ID Preview" className="max-h-32 rounded-md" />
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Selfie with ID and handwritten note</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, 'selfieFile')}
                    className="w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-500 file:text-white hover:file:bg-[#45c0be]"
                    required
                  />
                  {filePreviews.selfieFile && (
                    <div className="mt-2">
                      <img src={filePreviews.selfieFile} alt="Selfie Preview" className="max-h-32 rounded-md" />
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h3 className="text-xl font-semibold text-blue-200">Project Transparency</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Official Website</label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Twitter / X Profile</label>
                  <input
                    type="url"
                    name="twitter"
                    value={formData.twitter}
                    onChange={handleChange}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">GitHub or Repository Link (optional)</label>
                  <input
                    type="url"
                    name="github"
                    value={formData.github}
                    onChange={handleChange}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Have you launched any previous projects? (optional)</label>
                  <textarea
                    name="previousProjects"
                    value={formData.previousProjects}
                    onChange={handleChange}
                    rows={2}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Why should your project receive the GoldCheck Mark? (max 200 characters)</label>
                  <textarea
                    name="justification"
                    value={formData.justification}
                    onChange={handleChange}
                    rows={3}
                    maxLength={200}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <div className="text-xs text-gray-400 text-right">{formData.justification.length}/200</div>
                </div>
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <h3 className="text-xl font-semibold text-blue-200">Consent & Signature</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      type="checkbox"
                      id="consent1"
                      name="consent1"
                      checked={formData.consent1}
                      onChange={handleChange}
                      className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-600 rounded bg-gray-700"
                      required
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="consent1" className="font-medium text-gray-300">
                      I confirm that all submitted data is accurate and may be used for internal KYC review.
                    </label>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      type="checkbox"
                      id="consent2"
                      name="consent2"
                      checked={formData.consent2}
                      onChange={handleChange}
                      className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-600 rounded bg-gray-700"
                      required
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="consent2" className="font-medium text-gray-300">
                      I understand that KYC verification does not guarantee payment or project approval.
                    </label>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      type="checkbox"
                      id="consent3"
                      name="consent3"
                      checked={formData.consent3}
                      onChange={handleChange}
                      className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-600 rounded bg-gray-700"
                      required
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="consent3" className="font-medium text-gray-300">
                      I acknowledge that only verified and legitimate projects will receive the GoldCheck Mark.
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Digital Signature (Type Full Name)</label>
                  <input
                    type="text"
                    name="signature"
                    value={formData.signature}
                    onChange={handleChange}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Date</label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-4">
          <div>
            {step > 1 && (
              <button
                onClick={prevStep}
                className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Back
              </button>
            )}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              Cancel
            </button>
            {step < 4 ? (
              <button
                onClick={nextStep}
                disabled={!validateStep(step)}
                className={`px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${validateStep(step) ? 'bg-[#45c0be] hover:bg-[#35b0ae] text-white' : 'bg-gray-600 text-gray-400 cursor-not-allowed'}`}
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!validateStep(4) || isSubmitting}
                className={`px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${validateStep(4) ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-gray-600 text-gray-400 cursor-not-allowed'}`}
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <ClipLoader size={16} color="#ffffff" className="mr-2" />
                    Submitting...
                  </div>
                ) : (
                  'Submit'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default KYCModal;